import { Component, type ErrorInfo, type ReactNode, useId } from "react";
import { createRoot } from "react-dom/client";

interface AppRecoveryBoundaryProps {
  children: ReactNode;
}

interface AppRecoveryBoundaryState {
  failed: boolean;
}

interface AppRecoveryPanelProps {
  incidentId: string;
  onReload: () => void;
}

export function recoveryIncidentId(now = new Date(), random: () => number = Math.random): string {
  const day = now.toISOString().slice(0, 10).replaceAll("-", "");
  const suffix = Math.floor(random() * 36 ** 6)
    .toString(36)
    .toUpperCase()
    .padStart(6, "0")
    .slice(-6);
  return `REC-${day}-${suffix}`;
}

export function AppRecoveryPanel({ incidentId, onReload }: AppRecoveryPanelProps) {
  const titleId = useId();
  return (
    <main className="app-recovery-shell" aria-labelledby={titleId}>
      <section className="app-recovery-card" role="alert">
        <p className="app-recovery-eyebrow">安全恢复</p>
        <h1 id={titleId}>系统页面需要重新载入</h1>
        <p>
          当前页面发生异常，系统已停止继续处理页面操作。不要重复提交收款、退款、充值或库存操作；重新进入后请先按订单号或请求号核对最终结果。
        </p>
        <p className="app-recovery-help">
          如果重新载入后仍然出现此页面，请把故障编号 <strong>{incidentId}</strong>
          告诉维护人员。页面不会显示密码、资金明细或技术堆栈。
        </p>
        <button type="button" className="primary-button" onClick={onReload}>
          重新载入系统
        </button>
      </section>
    </main>
  );
}

export class AppRecoveryBoundary extends Component<
  AppRecoveryBoundaryProps,
  AppRecoveryBoundaryState
> {
  state: AppRecoveryBoundaryState = { failed: false };
  private readonly incidentId = recoveryIncidentId();

  static getDerivedStateFromError(_error: unknown): AppRecoveryBoundaryState {
    return { failed: true };
  }

  componentDidCatch(_error: unknown, _info: ErrorInfo): void {
    console.error("ERP_RENDER_RECOVERY", { incidentId: this.incidentId });
  }

  render() {
    if (this.state.failed) {
      return (
        <AppRecoveryPanel incidentId={this.incidentId} onReload={() => window.location.reload()} />
      );
    }
    return this.props.children;
  }
}

export function renderBootstrapRecovery(root: HTMLElement): void {
  const incidentId = recoveryIncidentId();
  console.error("ERP_BOOTSTRAP_RECOVERY", { incidentId });
  createRoot(root).render(
    <AppRecoveryPanel incidentId={incidentId} onReload={() => window.location.reload()} />,
  );
}
